import { Contact } from '@bumble/app/__generated__/data-contracts';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';

interface ContactUpdateProps {
  onSubmit: () => void;
  contact: Contact;
}

export const ContactUpdate = ({ onSubmit, contact }: ContactUpdateProps) => {
  const api = useApi();
  const [firstName, setFirstName] = useState(contact?.first_name || '');
  const [lastName, setLastName] = useState(contact?.last_name || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [phone, setPhone] = useState(contact?.phone || '');
  const [address, setAddress] = useState(contact?.address || '');
  const [companyName, setCompanyName] = useState(contact?.company_name || '');
  const [type, setType] = useState(contact?.contact_type || '');

  const { data } = useQuery(
    ['contacttypes'],
    api.contactTypesApiV1ContactTypesGet.bind(null, {}),
    { enabled: true },
  );

  const updateContact = useMutation(api.updateContactApiV1UpdateContactPost, {
    onSuccess() {
      toast.success('Contact successfully updated.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    updateContact.mutate({
      contact_id: contact?.contact_id,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      address: address,
      company_name: companyName,
      contact_type: type,
    });
  }

  return (
    <Box pt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            variant="outlined"
            sx={{ mb: 4 }}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            variant="outlined"
            sx={{ mb: 4 }}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Grid>
      </Grid>
      <TextField
        fullWidth
        type='email'
        label="Email"
        variant="outlined"
        sx={{ mb: 4 }}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type='tel'
            label="Phone"
            variant="outlined"
            sx={{ mb: 4 }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Company Name"
            variant="outlined"
            sx={{ mb: 4 }}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </Grid>
      </Grid>
      <TextField
        fullWidth
        label="Address"
        variant="outlined"
        sx={{ mb: 4 }}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        multiline
        rows={2}
      />
      <FormControl fullWidth>
        <InputLabel id="contact_type_label">Type</InputLabel>
        <Select
          labelId="contact_type_label"
          id="contact_type"
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          {data?.data.map((t: string) => (
            <MenuItem value={t} key={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={email === '' || !type}>
          Save Contact
        </Button>
      </Box>
    </Box>
  );
};
